// ...existing code...
import dayjs from "dayjs";

export const formatAlerts = (val) => {
  const n = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(n)) return "—";
  if (n <= 0) return "0 alert";    // or "No alerts"
  if (n === 1) return "1 alert";
  return `${n} alerts`;
};
export const toArray = (x) => {
  if (Array.isArray(x)) return x;
  if (Array.isArray(x?.points)) return x.points;
  if (Array.isArray(x?.series)) return x.series;
  if (Array.isArray(x?.data)) return x.data;
  return [];
};

export const parseDate = (obj) => {
  const raw =
    obj?.timestamp ||
    obj?.date ||
    obj?.day ||
    obj?.busDate ||
    obj?.Date ||
    obj?.Day ||
    obj?.BusDate;
  if (!raw) return null;
  const d = dayjs(raw);
  return d.isValid() ? d.startOf("day") : null;
};

export const ensurePct = (x) => {
  const n = Number(x);
  if (!isFinite(n)) return 0;
  if (n <= 1) return Math.round(n * 100);
  return Math.round(n);
};

export const clamp = (n, min, max) => Math.min(max, Math.max(min, Math.round(Number(n) || 0)));

export const downsample = (arr = [], step = 2) => {
  const out = [];
  for (let i = 0; i < arr.length; i += step) out.push(arr[i]);
  return out;
};

/* Pick the numeric "unhealthy" field from variable schemas */
export const pickUnhealthyCount = (d) => {
  if (typeof d?.unhealthy === "number") return Math.max(0, Math.round(d.unhealthy));
  if (typeof d?.unhealthyCount === "number") return Math.max(0, Math.round(d.unhealthyCount));
  if (typeof d?.alerts === "number") return Math.max(0, Math.round(d.alerts));
  if (typeof d?.totalUnhealthy === "number") return Math.max(0, Math.round(d.totalUnhealthy));
  if (typeof d?.count === "number") return Math.max(0, Math.round(d.count));
  if (typeof d?.value === "number") return Math.max(0, Math.round(d.value));
  return null;
};

/* Map a store data point to 0..20 "score" */
export const mapStoreScore = (d) => {
  if (typeof d?.score === "number") return clamp(d.score, 0, 20);
  if (typeof d?.value === "number") return clamp(d.value, 0, 20);
  if (typeof d?.availability === "number")
    return clamp(d.availability <= 1 ? d.availability * 20 : d.availability / 5, 0, 20);
  if (typeof d?.successRate === "number") return clamp(ensurePct(d.successRate) / 5, 0, 20);
  if (typeof d?.uptime === "number") return clamp(ensurePct(d.uptime) / 5, 0, 20);

  const s = (d?.status || "").toLowerCase();
  if (s.includes("down")) return 0;
  if (s.includes("degrad")) return 12;
  if (s.includes("healthy") || s.includes("up")) return 20;
  return 0;
};

/* Map a service data point to 0..100% */
export const mapServicePercent = (d) => {
  if (typeof d?.percent === "number") return clamp(ensurePct(d.percent), 0, 100);
  if (typeof d?.value === "number") return clamp(ensurePct(d.value), 0, 100);
  if (typeof d?.successRate === "number") return clamp(ensurePct(d.successRate), 0, 100);
  if (typeof d?.availability === "number") return clamp(ensurePct(d.availability), 0, 100);
  if (typeof d?.uptime === "number") return clamp(ensurePct(d.uptime), 0, 100);

  const s = (d?.status || "").toLowerCase();
  if (s.includes("down")) return 0;
  if (s.includes("degrad")) return 80;
  if (s.includes("healthy") || s.includes("up")) return 100;
  return 0;
};

/* Build sorted union of ISO dates (YYYY-MM-DD) and return labels + usedDates + maps */
export const buildDateIndex = ({ keys = [], dataByKey = {}, days = 30, downsampleStep = 2, labelFormat = "MMM D" }) => {
  const maps = {};
  for (const k of keys) maps[k] = new Map();

  for (const k of keys) {
    const arr = toArray(dataByKey?.[k]);
    for (const item of arr) {
      const d = parseDate(item);
      if (!d) continue;
      maps[k].set(d.format("YYYY-MM-DD"), item);
    }
  }

  const allDates = Array.from(new Set(Object.values(maps).flatMap((m) => Array.from(m.keys())))).sort();
  const used = allDates.slice(-days);
  const sampled = downsample(used, downsampleStep);
  const labels = sampled.map((iso) => dayjs(iso).format(labelFormat).toLowerCase());
  return { maps, usedDates: sampled, labels };
};

/* Align a map of items (map of iso->item) into a numeric array by mapping function */
export const alignSeriesFromMap = (map, usedDates, valueMapper) =>
  usedDates.map((iso) => {
    const item = map.get(iso);
    if (!item) return null;
    const v = valueMapper(item);
    return typeof v === "number" ? v : null;
  });

/* Responsive chart height helper */
export const computeChartHeight = (w) => {
  if (!w) return 240;
  if (w >= 1400) return 480;
  if (w >= 1100) return 360;
  if (w >= 900) return 360;
  if (w >= 600) return 300;
  return 220;
};

/* Default tooltip formatter (html string) — shows axis label, series values and optional per-point metadata store/storeName */
export const defaultTooltipFormatter = (unit) => (params) => {
  if (!Array.isArray(params)) params = [params];
  const header = `<div style="margin-bottom:6px;"><strong>${params[0]?.axisValueLabel ?? params[0]?.name ?? ""}</strong></div>`;
  const rows = params
    .map((p) => {
      const val = p?.value ?? "—";
      const suffix = unit ? ` ${unit}` : "";
      const metaStore = p?.data && (p.data.store || p.data.storeName) ? ` — ${String(p.data.store || p.data.storeName)}` : "";
      return `<div style="margin:2px 0;">${p.marker} ${p.seriesName}: <strong>${val}${suffix}</strong>${metaStore}</div>`;
    })
    .join("");
  return header + rows;
};

// // Map a point to 0..100%: prefer numeric fields, fallback to status text
export const  mapPercent= (d) => {
  if (typeof d?.percent === "number") return clamp(ensurePct(d.percent), 0, 100);
  if (typeof d?.value === "number") return clamp(ensurePct(d.value), 0, 100);
  if (typeof d?.successRate === "number") return clamp(ensurePct(d.successRate), 0, 100);
  if (typeof d?.availability === "number") return clamp(ensurePct(d.availability), 0, 100);
  if (typeof d?.uptime === "number") return clamp(ensurePct(d.uptime), 0, 100);

  const s = (d?.status || "").toLowerCase();
  if (s.includes("down")) return 0;
  if (s.includes("degrad")) return 80;
  if (s.includes("healthy") || s.includes("up") || s.includes("ok")) return 100;
  return 0;
}
